import { Grid, Typography } from '@mui/material'
import { GetStaticProps } from 'next'

import { Testimonial } from '@ecm/data/Testimonial'
import { runEff } from '@ecm/effect'
import { FlowConnector, FlowFrame, FlowGrid } from '@ecm/ui/component/grid/Flow'
import CloudflareImage from '@ecm/ui/element/CloudflareImage'
import Color from '@ecm/ui/element/Color'
import Feature from '@ecm/ui/element/Feature'
import MainContainer from '@ecm/ui/element/MainContainer'
import TestimonialCarousel from '@ecm/ui/element/Testimonial'
import DefaultLayout from '@ecm/ui/layout/Default'

import mailboxIllustration from '/public/images/illustration/mailbox.svg'
import stackOfMailIllustration from '/public/images/illustration/stack-of-mail.svg'

type VirtualPoBoxPageProps = {
  testimonial?: Testimonial
}

export const VirtualPoBoxPage = ({ testimonial }: VirtualPoBoxPageProps) => (
  <DefaultLayout
    finalCta="Get a PO box today"
    finalCtaHeader="Skip the line. Get your time back with a PO box online."
    seoPage="virtual-po-box"
  >
    <Grid
      container
      bgcolor={Color.smoke.normal}
      justifyContent="center"
      sx={{ paddingY: { md: '4em', xs: '2em' } }}
    >
      <Feature
        asHero
        description="Forward postal mail to our secure processing facility using our virtual PO boxes and access digitized postal mail 24/7, on any device."
        illustrationSrc={mailboxIllustration}
        imagePosition="right"
        primaryCta="Get a PO box today"
        primaryHref="/pricing"
        title="From Your PO Box to Your Inbox"
      />
    </Grid>

    <MainContainer sx={{ paddingY: { md: '4em', xs: '2em' } }}>
      <Grid
        sx={{
          alignItems: 'center',
          display: 'grid',
          gap: '2em 4em',
          gridTemplateColumns: { md: 'repeat(2, 1fr)', sm: '1fr', xs: '1fr' },
          gridTemplateRows: 'auto',
          justifyItems: { md: 'center', sm: 'center', xs: 'start' },
        }}
      >
        <CloudflareImage src={stackOfMailIllustration} />

        <Grid container flexDirection="column" maxWidth="25em">
          <Typography variant="h4" component="h2">
            Access postal mail anytime
          </Typography>
          <Typography variant="p2">
            The Virtual PO Box offers individuals and small businesses a way to digitize postal mail
            using our vast network of PO boxes and smart technology.
          </Typography>
          <Typography variant="p2">
            All your postal mail is digitized. Access from anywhere. If you want the physical copy,
            we will forward it to you.
          </Typography>
        </Grid>
      </Grid>
    </MainContainer>

    <Grid
      container
      bgcolor={Color.smoke.normal}
      sx={{ paddingY: { md: '4em', xs: '2em' } }}
      justifyContent="center"
    >
      <MainContainer
        sx={{
          display: 'grid',
          gap: '2em',
          gridTemplateColumns: { md: 'repeat(3, 1fr)', sm: '1fr', xs: '1fr' },
          gridTemplateRows: 'auto',
          justifyItems: { md: 'center', xs: 'start' },
        }}
      >
        <Grid
          container
          flexDirection="column"
          justifySelf={{ md: 'start', sm: 'center' }}
          maxWidth="25em"
          alignItems="flex-start"
        >
          <CloudflareImage src="/images/icon/logomark.svg" width={80} height={80} />
          <Typography variant="h5" component="h3">
            Easier than a standard PO Box
          </Typography>
          <Typography variant="p3">
            Access the virtual PO box anywhere. Get digital copies of your postal mail, search them
            just like email, and forward the physical copies.
          </Typography>
        </Grid>

        <Grid
          container
          flexDirection="column"
          justifySelf={{ md: 'start', sm: 'center' }}
          maxWidth="25em"
          alignItems="flex-start"
        >
          <CloudflareImage src="/images/icon/scan.svg" width={80} height={80} />
          <Typography variant="h5" component="h3">
            Maintain your privacy
          </Typography>
          <Typography variant="p3">
            With our virtual PO box service, all of the digital information is secured with
            bank-grade encryption, and secure shredding is done onsite.
          </Typography>
        </Grid>

        <Grid
          container
          flexDirection="column"
          justifySelf={{ md: 'start', sm: 'center' }}
          maxWidth="25em"
          alignItems="flex-start"
        >
          <CloudflareImage src="/images/icon/action.svg" width={80} height={80} />
          <Typography variant="h5" component="h3">
            Perfect for growing businesses
          </Typography>
          <Typography variant="p3">
            Start with one PO box online and add more as your business grows or changes. Choose from
            a network of over 50 PO box addresses.
          </Typography>
        </Grid>
      </MainContainer>
    </Grid>

    <MainContainer sx={{ paddingY: { md: '4em', xs: '2em' } }}>
      <Typography variant="h3" component="h2" textAlign="center">
        How It Works
      </Typography>

      <FlowGrid frameCount={3}>
        <FlowFrame heading="Pick a plan that works for you" icon="logomark" index={1}>
          Our plans are designed to grow with you. Pick a PO box plan based on your needs and start
          accessing your postal mail online.
        </FlowFrame>

        <FlowConnector icon="connector-1" width="90%" margin="3em auto 0 auto" index={1} />

        <FlowFrame heading="Access postal mail and act on it" icon="action" index={2}>
          Once postal mail is in your account, you're in control. We convert it into secure,
          searchable PDFs. Shred, store, or forward on any device.
        </FlowFrame>

        <FlowConnector icon="connector-2" width="90%" margin="0 auto 3em auto" index={2} />

        <FlowFrame heading="Add more as you need them" icon="location-pin" index={3}>
          Scale addresses as needed. Add a street address for deliveries from UPS/FedEx. Manage
          addresses, add users, store items in the cloud.
        </FlowFrame>
      </FlowGrid>
    </MainContainer>

    {testimonial && (
      <Grid paddingX={{ lg: '64px', md: '40px', sm: '24px', xs: '16px' }}>
        <TestimonialCarousel testimonials={[testimonial]} />
      </Grid>
    )}
  </DefaultLayout>
)

export const getStaticProps: GetStaticProps = async () => {
  const testimonial = await runEff(async eff => {
    try {
      return await eff.getTestimonial('audra-mcmurray')
    } catch (e) {
      return null
    }
  })

  return { props: { testimonial } }
}

export default VirtualPoBoxPage
